<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { Toaster } from 'vue-sonner'
import { DEFAULT_TOAST_DURATION } from '@/constants/ui'
import { useThemeStore } from '@/stores/theme'
import type { Props } from './type'

defineOptions({
  name: 'AppToast',
})

defineProps<Props>()

const themeStore = useThemeStore()
const { currentTheme } = storeToRefs(themeStore)

const toasterTheme = computed(() => currentTheme.value)
const toastOptions = {
  class: 'dreama-toast',
  descriptionClass: 'dreama-toast__description',
}
</script>

<template>
  <Toaster
    class="dreama-sonner"
    :theme="toasterTheme"
    position="top-center"
    :duration="DEFAULT_TOAST_DURATION"
    :visible-toasts="3"
    :offset="16"
    :mobile-offset="12"
    :toast-options="toastOptions"
  />
</template>

<style>
[data-sonner-toaster].dreama-sonner {
  --border-radius: 18px;
  --width: min(420px, calc(100vw - 24px));
  --normal-bg: var(--app-surface-raised);
  --normal-border: var(--app-border-strong);
  --normal-text: var(--app-text-main);
  --success-bg: var(--normal-bg);
  --success-border: var(--normal-border);
  --success-text: var(--normal-text);
  --error-bg: var(--normal-bg);
  --error-border: var(--normal-border);
  --error-text: var(--normal-text);
  --warning-bg: var(--normal-bg);
  --warning-border: var(--normal-border);
  --warning-text: var(--normal-text);
  --info-bg: var(--normal-bg);
  --info-border: var(--normal-border);
  --info-text: var(--normal-text);
}

[data-sonner-toaster].dreama-sonner [data-sonner-toast][data-styled='true'] {
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid var(--normal-border);
  background: var(--normal-bg);
  color: var(--normal-text);
  box-shadow: var(--app-shadow-floating);
  backdrop-filter: blur(24px);
}

[data-sonner-toaster].dreama-sonner [data-title] {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: 0.01em;
}

[data-sonner-toaster].dreama-sonner [data-description],
[data-sonner-toaster].dreama-sonner .dreama-toast__description {
  margin-top: 4px;
  color: var(--app-text-soft);
  font-size: 13px;
  line-height: 1.45;
}

[data-sonner-toaster].dreama-sonner [data-icon] {
  color: var(--app-text-soft);
}

[data-sonner-toaster].dreama-sonner [data-sonner-toast][data-type='success'] [data-icon] {
  color: var(--app-positive);
}

[data-sonner-toaster].dreama-sonner [data-sonner-toast][data-type='error'] [data-icon] {
  color: #f35c37;
}

@media (max-width: 640px) {
  [data-sonner-toaster].dreama-sonner {
    --width: min(100vw - 16px, 420px);
  }

  [data-sonner-toaster].dreama-sonner [data-sonner-toast][data-styled='true'] {
    padding: 14px 16px;
    border-radius: 16px;
  }
}
</style>
